import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toastActions } from "./toast";
import { getRandId, readURIFromFile } from "../libs";
import { ItemRemovalService } from "../api/itemRemoval";
import { currentUserActions } from "./currentUser";
import { MaxPhotoUpload, MaxUploadFileSize, MaxRegenCount, MaxPartialRegenCount } from "../constants";
import { OpenAPI } from "../api/core/OpenAPI";
import { NavigateFunction } from "react-router-dom";
import { applyWatermark, WatermarkOptions, dataURLtoBlob, blobToDataURL, fetchImageAsBlob, convertServiceSettingsToWatermarkOptions, isWatermarkEnabled, isAnnotationEnabled, convertServiceSettingsToAnnotationOptions, applyWatermarkAndAnnotation } from '../utils/watermark';
import { WATERMARK_TYPE } from './serviceSettings';
import { selectServiceSettings } from '../selectors';
import { ItemRemovalServerResponse, ThunkAPIType } from '../types/watermark';
/* eslint-enable */

// アセット画像をインポート
import pho_industrial from "../../assets/images/pho_industrial.webp";
import pho_luxury from "../../assets/images/pho_luxury.webp";
import pho_modern from "../../assets/images/pho_modern.webp";
import pho_nordic from "../../assets/images/pho_nordic.webp";

// TODO: テストが終わったら消す - モックテスト機能
// 使用方法: mockTestConfig.USE_MOCK_SERVER_RESPONSE を false にすると本番APIを使用
// テスト完了後は mockTestConfig オブジェクト全体と、それを使用している箇所を削除してください
const mockTestConfig = {
    // この値を true にすると、実際のサーバーレスポンスをモック化します
    // USE_MOCK_MODEがfalseの時のみ有効です
    USE_MOCK_SERVER_RESPONSE: false,

    // モック用の画像URL（実際の画像URLを使用）
    MOCK_IMAGE_URLS: [
        'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600',  // ソファのリビング
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600',  // モダンなリビング
        'https://images.unsplash.com/photo-1565182999561-18d7dc61c393?w=800&h=600', // 明るいリビング
    ],

    // サーバーレスポンスをモック化する関数
    createMockServerResponse(itemRemovalId: number, beforeImageId: number): ItemRemovalServerResponse {
        const mockUrl = this.MOCK_IMAGE_URLS[Math.floor(Math.random() * this.MOCK_IMAGE_URLS.length)]

        return {
            success: true,
            item_removal_id: itemRemovalId,
            before_image_id: beforeImageId,
            after_image_id: Math.floor(Math.random() * 100000) + 1000,
            after_image_url: mockUrl,
            after_image_file_name: `mock_image_${Date.now()}.jpg`,
            file_name: `mock_image_${Date.now()}.jpg`,
            headers: {
                'x-render-id': `mock-render-${Date.now()}`
            }
        }
    }
}


// 再実行用の画像配列
const regeneratedImages = [pho_modern, pho_luxury, pho_nordic, pho_industrial];


// 画像ごとのマスク情報
export interface ImageMaskData {
  maskLines: FurnitureMaskLine[];
  maskPreviewUrl: string | null;
  maskPreviewBlob: Blob | null;
  maskHistoryStack: FurnitureMaskLine[][];
  maskHistoryIndex: number;
}

export interface ItemImage {
  id?: number;
  before_image_id?: number;
  stubId: number;
  file: File;
  uri: string;
  serverURI?: string;
  isProcessedFail: boolean;
  isProcessed: boolean;
  maxRegenCount?: number;
  render_id?: string;
  // 画像ごとのマスク情報
  maskData?: ImageMaskData;
  // 一部モードで処理されたかどうか（trueの場合、再実行も一部モードのみ）
  processedWithPartialMode?: boolean;
}

export interface AfterImage {
  id: number;
  item_image_id: number;
  before_image_id?: number;
  serverURI: string;
  fileName: string;
  stubId?: number;
  // ウォーターマーク関連
  watermarkEnabled?: boolean;
  watermarkedURI?: string;
  isProcessingWatermark?: boolean;
}

// マスク描画用のLine型（AIEraserと同じ）
export interface FurnitureMaskLine {
  head: 'circle' | 'triangle' | 'square';
  headSize: number;
  points: number[];
}

export interface ItemRemovalState {
  isLoading: boolean;
  stubId: number;
  selectedItemImage?: number;
  selectedAfterImage?: number;
  itemImages: ItemImage[];
  afterImages: AfterImage[];
  isConfirmPopupShow: boolean;
  isPhotoTooBigPopupShow: boolean;
  isRegenPopupShow: boolean;
  isClearAllPopupShow: boolean;
  maxExecTimePerImage: number; // seconds
  runTime: number;
  selectedRegenImage?: number;
  afterImageRef: HTMLImageElement | null;
  beforeImageRef: HTMLImageElement | null;
  // ウォーターマーク関連
  watermarkGlobalEnabled: boolean;
  processingWatermarks: string[]; // 処理中の画像ID

  // 部分消去機能関連
  furnitureRemovalMode: 'full' | 'partial';       // 消去モード
  showFurnitureRemovalDrawing: boolean;           // 描画画面表示フラグ
  furnitureMaskLines: FurnitureMaskLine[];        // 確定済みの線
  tempFurnitureMaskLines: FurnitureMaskLine[];    // 描画中の一時線
  furnitureBrushHead: 'circle' | 'square' | 'triangle';
  furnitureBrushSize: number;
  furnitureMaskHistoryStack: FurnitureMaskLine[][];  // 履歴管理（Undo/Redo用）
  furnitureMaskHistoryIndex: number;
  showFurnitureRemovalConfirm: boolean;           // 実行確認ダイアログ
  isFurnitureRemovalProcessing: boolean;          // 処理中フラグ
  furnitureRemovalOriginalImageUrl: string | null; // 描画画面用の元画像URL
  furnitureMaskStageRef: any;                       // Konvaステージの参照
  furnitureMaskPreviewUrl: string | null;           // マスクプレビュー画像URL
  furnitureMaskPreviewBlob: Blob | null;            // マスク画像のBlob（API送信用）
  furnitureMaskIsReEdit: boolean;                   // 再編集フラグ（trueの場合マスクをクリアしない）
}

// アプリケーションのRootState型
interface RootState {
  itemRemoval: ItemRemovalState;
  [key: string]: unknown;
}

// サーバー応答の型
type ThunkDispatchType = (action: unknown) => void;
interface ThunkAPIType {
  getState: () => RootState;
  dispatch: ThunkDispatchType;
}

// FileListを受け取り、処理を行う非同期アクション
const addItemImages = createAsyncThunk<void, {stubId: string, files: FileList}, { state: RootState }>(
  "addItemImages",
  async (params, thunkAPI) => {
    const { itemImages } = thunkAPI.getState().itemRemoval;
    const { files, stubId } = params;
    const newImages: ItemImage[] = [...itemImages];

    for (const file of files) {
      if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/webp")
        continue;
      if (file.size >= MaxUploadFileSize) {
        thunkAPI.dispatch(itemRemovalActions.setIsPhotoTooBigPopupShow(true));
        continue;
      }
      if (newImages.length >= MaxPhotoUpload) break;
      const uri = await readURIFromFile(file);
      // 各画像ごとに新しいstubIdを生成してユニークさを保証
      const uniqueStubId = getRandId();
      newImages.push({
        stubId: uniqueStubId,
        file,
        uri,
        isProcessed: false,
        isProcessedFail: false,
        maxRegenCount: MaxRegenCount,
      });
    }
    thunkAPI.dispatch(itemRemovalActions.setItemImages(newImages));

    // 画像が追加された場合、最後に追加された画像を選択状態にする
    if (newImages.length > itemImages.length) {
      const lastAddedImage = newImages[newImages.length - 1];
      thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(lastAddedImage.stubId));
    }

    // stubIdをstateに保存して後でprocessItemRemovalで使用できるようにする
    if (stubId) {
      thunkAPI.dispatch(itemRemovalActions.setStubId(parseInt(stubId, 10) || getRandId()));
    }
  }
);

// 家具削除処理を実行する非同期アクション
const processItemRemoval = createAsyncThunk<void, NavigateFunction, { state: RootState }>(
  "processItemRemoval",
  async (navigate, thunkAPI) => {
    const { stubId, itemImages } = thunkAPI.getState().itemRemoval;
    // 処理前に未処理の画像があるか確認
    const unprocessedImages = itemImages.filter(img => !img.isProcessed);

    if (unprocessedImages.length === 0) {
      return;
    }

    thunkAPI.dispatch(itemRemovalActions.setIsLoading(true));

    // タイマー処理
    let runTime = 0;
    thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime += 1));
    }, 1000);

    try {
      // 画像処理実行
      const results = await itemImagesProcess(thunkAPI, itemImages, stubId.toString());

      // 成功した画像の数を確認
      const successCount = results.filter(r => r).length;
      if (successCount > 0) {
        // thunkAPI.dispatch(toastActions.toastSuccess(`${successCount}枚の画像処理が完了しました`));
      } else {
        // thunkAPI.dispatch(toastActions.toastError("画像処理に失敗しました"));
      }
    } catch (e) {
    } finally {
      // 処理終了時の後処理
      clearInterval(timer);
      thunkAPI.dispatch(itemRemovalActions.setIsLoading(false));
      thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate));
    }
  }
);


// すべての画像を処理する関数
async function itemImagesProcess(
  thunkAPI: ThunkAPIType,
  itemImages: ItemImage[],
  stubId: string
): Promise<boolean[]> {
  // 並列処理に変更（すべての画像を同時に処理する）

  const unprocessedImages = itemImages.filter(img => !img.isProcessed);


  // 最初の画像を選択状態に設定（未選択の場合のみ）
  if (unprocessedImages.length > 0 && thunkAPI.getState().itemRemoval.selectedItemImage === undefined) {
    thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(unprocessedImages[0].stubId));
  }

  // 各画像の処理をPromiseの配列として準備
  const processingPromises = unprocessedImages.map(itemImage => {

    // 各画像を並列処理
    return itemImageProcess(itemImage, thunkAPI, stubId)
      .then(result => {
        return { stubId: itemImage.stubId, success: result };
      })
      .catch(error => {
        console.error(`itemImagesProcess - 画像処理エラー:`, {
          stubId: itemImage.stubId,
          error
        });
        return { stubId: itemImage.stubId, success: false };
      });
  });

  // すべての処理が完了するのを待つ（各処理は独立して実行される）
  const results = await Promise.all(processingPromises);

  // booleanの配列に変換して返す
  return results.map(r => r.success);
}

// 単一の家具画像を処理する関数
function itemImageProcess(
  itemImage: ItemImage,
  thunkAPI: ThunkAPIType,
  stubId: string
): Promise<boolean> {
  return new Promise((resolve) => {
    const { file, maskData } = itemImage;

    // マスクデータがあるかどうかを判定
    const hasMask = !!(maskData?.maskPreviewBlob);

    // processItemRemovalメソッドを使用
    (async () => {
      try {

        // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
        let res;
        if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
          // モックレスポンスを使用
          await new Promise(resolve => setTimeout(resolve, 1500)); // API呼び出しをシミュレート
          const mockItemRemovalId = Math.floor(Math.random() * 10000) + 1;
          const mockBeforeImageId = itemImage.id || Math.floor(Math.random() * 10000) + 100;
          res = mockTestConfig.createMockServerResponse(mockItemRemovalId, mockBeforeImageId);
        } else {
          // マスクの有無で呼び出すAPIを分岐
          if (hasMask) {
            // 一部家具消しAPI（マスク付き）
            res = await ItemRemovalService.processMaskedItemRemoval({
              formData: {
                stub_id: stubId,
                item_image: file,
                mask_image: maskData.maskPreviewBlob,
              }
            });
          } else {
            // 全体家具消しAPI
            res = await ItemRemovalService.processItemRemoval({
              formData: {
                stub_id: stubId,
                item_image: file
              }
            });
          }
        }
        // TODO: テストが終わったら消す - サーバーレスポンスモックの終了


        const processResult = processServerResponse(
          itemImage.stubId,
          res,
          thunkAPI,
          hasMask  // マスク使用フラグを渡す
        );

        if (!processResult) {
          resolve(false);
          return;
        }
        resolve(true);
      } catch (error) {
        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
          ? error.message
          : '不明なエラーが発生しました';
        // thunkAPI.dispatch(toastActions.toastError(errorMessage));

        // この画像のみを失敗状態に設定
        thunkAPI.dispatch(itemRemovalActions.updateItemImageByStubId({
          stubId: itemImage.stubId,
          updates: { isProcessedFail: true }
        }));
        resolve(false);
      }
    })();
  });
}

// サーバーからの応答を処理する関数
function processServerResponse(
  stubId: number,
  res: ItemRemovalServerResponse,
  thunkAPI: ThunkAPIType,
  hasMask: boolean = false  // マスク使用フラグ（一部モードで処理された場合true）
): boolean {
  // 関数先頭で必要な値をすべて取得（並列処理中の状態変更の影響を受けないようにする）
  const state = thunkAPI.getState().itemRemoval;
  const { watermarkGlobalEnabled, selectedItemImage } = state;

  // ヘッダーからrender_idを取得
  const render_id = res.headers?.['x-render-id'] || '';

  // モックモードの場合
  if (OpenAPI.USE_MOCK) {
    const mockId = res.id || 999;
    const randomImage = regeneratedImages[Math.floor(Math.random() * regeneratedImages.length)];

    // この画像のみを更新
    thunkAPI.dispatch(itemRemovalActions.updateItemImageByStubId({
      stubId,
      updates: {
        isProcessedFail: false,
        isProcessed: true,
        id: mockId,
        serverURI: randomImage,
        render_id,
        processedWithPartialMode: hasMask,
        maxRegenCount: hasMask ? MaxPartialRegenCount : MaxRegenCount
      }
    }));
    return true;
  }

  // 実際のAPI処理 - 失敗時
  if (!res.success) {
    const errorMessage = res.message || "画像の処理に失敗しました";
    thunkAPI.dispatch(toastActions.toastError(errorMessage));

    // この画像のみを失敗状態に
    thunkAPI.dispatch(itemRemovalActions.updateItemImageByStubId({
      stubId,
      updates: { isProcessedFail: true }
    }));
    return false;
  }

  // 成功時 - itemImageを更新
  thunkAPI.dispatch(itemRemovalActions.updateItemImageByStubId({
    stubId,
    updates: {
      isProcessedFail: false,
      isProcessed: true,
      id: res.item_removal_id,
      before_image_id: res.before_image_id,
      serverURI: res.after_image_url,
      render_id,
      processedWithPartialMode: hasMask,
      maxRegenCount: hasMask ? MaxPartialRegenCount : MaxRegenCount
    }
  }));

  // 処理に失敗した場合はここで終了
  if (!res.item_removal_id) {
    return false;
  }

  // afterImageを追加
  if (res.after_image_id && res.after_image_url) {
    thunkAPI.dispatch(itemRemovalActions.addAfterImage({
      id: res.after_image_id,
      item_image_id: res.item_removal_id,
      before_image_id: res.before_image_id,
      serverURI: res.after_image_url,
      fileName: res.after_image_file_name || res.file_name || `After Image`,
      stubId, // 元画像のstubIdを設定（重要：これが主な関連付け手段）
    }));
  }

  // ウォーターマークが有効な場合、新しく追加された画像に自動的にウォーターマークを適用
  // 先頭で取得した値を使用（並列処理中の状態変更の影響を受けない）
  if (watermarkGlobalEnabled && res.after_image_id && res.after_image_url) {
    const serviceSettings = selectServiceSettings(thunkAPI.getState());

    if (serviceSettings && (isWatermarkEnabled(serviceSettings) || isAnnotationEnabled(serviceSettings))) {
      // ウォーターマーク・注釈を適用
      thunkAPI.dispatch(applyWatermarkToImage({
        imageId: res.after_image_id,
        imageUrl: res.after_image_url
      }));
    }
  }

  // 新しく処理した画像が現在選択中の画像と一致していれば、自動的に処理後画像を選択
  // 先頭で取得した値を使用
  if (selectedItemImage === stubId && res.after_image_id) {
    thunkAPI.dispatch(itemRemovalActions.setSelectedAfterImage(res.after_image_id));
  }

  return true;
}

// selected項目の削除
const deleteSelectedItem = createAsyncThunk<void, void, { state: RootState }>(
  "deleteSelectedItem",
  async (_, thunkAPI) => {
    const { selectedItemImage, itemImages } = thunkAPI.getState().itemRemoval;
    if (selectedItemImage === undefined) return;

    // 選択された項目を除外した新しい配列を作成
    const newItemImages = itemImages.filter(
      (img: ItemImage) => img.stubId !== selectedItemImage
    );

    // 状態を更新
    thunkAPI.dispatch(itemRemovalActions.setItemImages(newItemImages));
    thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(undefined));
  }
);

// 全項目の削除
const deleteAllItems = createAsyncThunk<void, void, { state: RootState }>(
  "deleteAllItems",
  async (_, thunkAPI) => {
    thunkAPI.dispatch(itemRemovalActions.clearData());
  }
);

// 処理済み画像のクリア
const clearSelectedAfterImage = createAsyncThunk<void, void, { state: RootState }>(
  "clearSelectedAfterImage",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().itemRemoval;
    const { selectedItemImage, itemImages } = state;
    if (selectedItemImage === undefined) return;

    const selectedImg = itemImages.find((img) => img.stubId === selectedItemImage);
    if (!selectedImg || !selectedImg.id) return;

    try {
      // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
      let clearRes;
      if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
        // モックレスポンスを使用
        await new Promise(resolve => setTimeout(resolve, 1000)); // API呼び出しをシミュレート
        clearRes = { success: true };
      } else {
        // 実際のAPIを使用
        clearRes = await ItemRemovalService.clearAfterImages(selectedImg.id);
      }
      // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

      if (clearRes.success) {
        thunkAPI.dispatch(
          toastActions.toastSuccess("処理済み画像をクリアしました")
        );
      } else {
        thunkAPI.dispatch(
          toastActions.toastError(clearRes.message || "処理済み画像のクリアに失敗しました")
        );
      }
    } catch (error) {
      const errorMessage = typeof error === 'object' && error !== null && 'message' in error
        ? error.message
        : '処理済み画像のクリアに失敗しました';

      thunkAPI.dispatch(
        toastActions.toastError(errorMessage)
      );
    }
  }
);

// 処理済み画像の再生成
const regenSelectedAfterImage = createAsyncThunk<void, void, { state: RootState }>(
  "regenSelectedAfterImage",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().itemRemoval;
    const { selectedItemImage, itemImages } = state;

    // selectedItemImageがない場合は、最初の画像を選択
    if (selectedItemImage === undefined) {
      if (itemImages.length > 0) {
        const firstImg = itemImages[0];
        thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(firstImg.stubId));
        return thunkAPI.dispatch(itemRemovalActions.regenSelectedAfterImage());
      }
      return;
    }

    const selectedImg = itemImages.find((img) => img.stubId === selectedItemImage);

    if (!selectedImg.id) {
      thunkAPI.dispatch(toastActions.toastError("処理済みでない画像は再生成できません"));
      return;
    }

    thunkAPI.dispatch(itemRemovalActions.setSelectedRegenImage(selectedImg.stubId));
    thunkAPI.dispatch(itemRemovalActions.setIsLoading(true));

    // runTimeを0から始める - タイマー間隔を500ミリ秒に変更
    let runTime = 0;
    thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime += 0.5)); // 0.5秒ずつ増加
    }, 500); // 500ミリ秒ごとに更新

    try {
      // マスク画像を取得（一部モードの場合）
      const maskBlob = selectedImg.maskData?.maskPreviewBlob || null;

      // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
      let regenRes;
      if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
        // モックレスポンスを使用
        await new Promise(resolve => setTimeout(resolve, 1500)); // API呼び出しをシミュレート
        const mockUrl = mockTestConfig.MOCK_IMAGE_URLS[Math.floor(Math.random() * mockTestConfig.MOCK_IMAGE_URLS.length)];
        regenRes = {
          success: true,
          after_image_id: Math.floor(Math.random() * 100000) + 5000,
          after_image_url: mockUrl,
          after_image_file_name: `mock_image_${Date.now()}.jpg`,
          file_name: `item_removed_regen_${Date.now()}.jpg`
        };
      } else {
        // マスクの有無で呼び出すAPIを分岐
        if (maskBlob) {
          // 一部モード: /item-removal/masked/regen/{id}/
          regenRes = await ItemRemovalService.regenMaskedAfterImages(selectedImg.before_image_id, maskBlob);
        } else {
          // 全体モード: /item-removal/regen/{id}/
          regenRes = await ItemRemovalService.regenAfterImages(selectedImg.before_image_id);
        }
      }
      // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

      if (regenRes.success && regenRes.after_image_id && regenRes.after_image_url) {
        // API成功かつ画像データが正常に返された場合のみカウントを減らす
        thunkAPI.dispatch(itemRemovalActions.subtractMaxRegen(selectedImg.stubId));
        thunkAPI.dispatch(
          toastActions.toastSuccess("処理済み画像を再生成しました")
        );

        const { afterImages } = thunkAPI.getState().itemRemoval;
        const newAfterImageId = regenRes.after_image_id;

        // 既存画像を保持したまま、新しい画像を追加
        const newAfterImages = [...afterImages];

        // 新しい処理済み画像を追加
        const newAfterImage = {
            id: newAfterImageId,
            item_image_id: selectedImg.id,
            before_image_id: selectedImg.before_image_id,
            serverURI: regenRes.after_image_url,
            fileName: regenRes.after_image_file_name || regenRes.file_name || `Regenerated Image`,
            stubId: selectedImg.stubId,
        };

        newAfterImages.push(newAfterImage);

        // 状態を更新
        thunkAPI.dispatch(itemRemovalActions.setAfterImages(newAfterImages));

        // ウォーターマークが有効な場合、新しく生成された画像に自動的にウォーターマークを適用
        const { watermarkGlobalEnabled } = thunkAPI.getState().itemRemoval;
        if (watermarkGlobalEnabled && regenRes.after_image_id && regenRes.after_image_url) {
            const serviceSettings = selectServiceSettings(thunkAPI.getState());
            const hasWatermarkSettings = serviceSettings && (isWatermarkEnabled(serviceSettings) || isAnnotationEnabled(serviceSettings));

          if (hasWatermarkSettings) {
            // ウォーターマークを適用
            thunkAPI.dispatch(applyWatermarkToImage({
              imageId: regenRes.after_image_id,
              imageUrl: regenRes.after_image_url
            }));
          }
        }

        // 新しく生成した画像を選択状態にする
        thunkAPI.dispatch(itemRemovalActions.setSelectedAfterImage(newAfterImageId));
      } else {
        thunkAPI.dispatch(
          toastActions.toastError(regenRes.message || "再生成に失敗しました")
        );
      }
    } catch (error) {
      const errorMessage = typeof error === 'object' && error !== null && 'message' in error
        ? error.message
        : '処理済み画像の再生成に失敗しました';

      thunkAPI.dispatch(
        toastActions.toastError(errorMessage)
      );
    } finally {
      thunkAPI.dispatch(itemRemovalActions.setSelectedRegenImage(undefined));
      thunkAPI.dispatch(itemRemovalActions.setIsLoading(false));
      clearInterval(timer);  // タイマーをクリア
    }
  }
);

const initialState: ItemRemovalState = {
  isLoading: false,
  stubId: getRandId(),
  selectedItemImage: undefined,
  selectedAfterImage: undefined,
  itemImages: [],
  afterImages: [],
  isConfirmPopupShow: false,
  isPhotoTooBigPopupShow: false,
  isRegenPopupShow: false,
  isClearAllPopupShow: false,
  maxExecTimePerImage: 40,
  runTime: 0,
  selectedRegenImage: undefined,
  afterImageRef: null,
  beforeImageRef: null,
  // ウォーターマーク関連
  watermarkGlobalEnabled: false,
  processingWatermarks: [],
  // 部分消去機能関連
  furnitureRemovalMode: 'full',
  showFurnitureRemovalDrawing: false,
  furnitureMaskLines: [],
  tempFurnitureMaskLines: [],
  furnitureBrushHead: 'circle',
  furnitureBrushSize: 50,
  furnitureMaskHistoryStack: [[]],
  furnitureMaskHistoryIndex: 0,
  showFurnitureRemovalConfirm: false,
  isFurnitureRemovalProcessing: false,
  furnitureRemovalOriginalImageUrl: null,
  furnitureMaskStageRef: null,
  furnitureMaskPreviewUrl: null,
  furnitureMaskPreviewBlob: null,
  furnitureMaskIsReEdit: false,
};


// ウォーターマーク適用の非同期処理
const applyWatermarkToImage = createAsyncThunk<
  { imageId: number; watermarkedURI: string },
  { imageId: number; imageUrl: string },
  { state: RootState }
>(
  'itemRemoval/applyWatermarkToImage',
  async ({ imageId, imageUrl }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch
    const state = thunkAPI.getState()

    dispatch(itemRemovalActions.setImageWatermarkProcessing({ imageId, isProcessing: true }))

    try {
      // サービス設定からウォーターマーク設定を取得
      const watermarkSettings = selectServiceSettings(state)

      // ウォーターマークまたは注釈が有効かチェック
      if (!isWatermarkEnabled(watermarkSettings) && !isAnnotationEnabled(watermarkSettings)) {
        throw new Error('Watermark/annotation is not enabled in service settings')
      }

      // 画像をBlobに変換
      const blob = await fetchImageAsBlob(imageUrl)

      // ウォーターマーク・注釈オプションを構築
      const watermarkOpts = isWatermarkEnabled(watermarkSettings)
        ? convertServiceSettingsToWatermarkOptions(watermarkSettings)
        : null
      const annotationOpts = isAnnotationEnabled(watermarkSettings)
        ? convertServiceSettingsToAnnotationOptions(watermarkSettings)
        : null

      const result = await applyWatermarkAndAnnotation(
        blob,
        watermarkOpts,
        annotationOpts,
        blob.size / (1024 * 1024)
      )

      // Blob URLではなくData URLを作成（CSP対策）
      const watermarkedURI = await blobToDataURL(result.blob)

      dispatch(itemRemovalActions.setImageWatermarkStatus({
        imageId,
        enabled: true,
        watermarkedURI
      }))

      return { imageId, watermarkedURI }
    } catch (error) {
      console.error('Failed to apply watermark:', error)
      dispatch(toastActions.toastError('ウォーターマークの適用に失敗しました'))
      throw error
    } finally {
      dispatch(itemRemovalActions.setImageWatermarkProcessing({ imageId, isProcessing: false }))
    }
  }
)

// 全画像へのウォーターマーク一括適用/解除
const toggleAllWatermarks = createAsyncThunk<
  void,
  boolean,
  { state: RootState }
>(
  'itemRemoval/toggleAllWatermarks',
  async (enabled, thunkAPI) => {
    const state = thunkAPI.getState()
    const dispatch = thunkAPI.dispatch
    const { afterImages } = state.itemRemoval

    dispatch(itemRemovalActions.setWatermarkGlobalEnabled(enabled))

    if (enabled) {
      // 全画像にウォーターマーク適用
      const promises = afterImages.map((image: AfterImage) =>
        dispatch(applyWatermarkToImage({
          imageId: image.id,
          imageUrl: image.serverURI
        }))
      )

      await Promise.all(promises)
    } else {
      // 全画像のウォーターマークを除去
      // Data URLの場合はrevokeObjectURLは不要
      dispatch(itemRemovalActions.clearAllWatermarks())
    }
  }
)

// マスクプレビューを生成する非同期アクション
const generateFurnitureMaskPreview = createAsyncThunk<void, void, { state: RootState }>(
  "generateFurnitureMaskPreview",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().itemRemoval;
    const {
      selectedItemImage,
      itemImages,
      furnitureMaskLines,
      furnitureMaskStageRef,
      furnitureRemovalOriginalImageUrl,
      furnitureMaskHistoryStack,
      furnitureMaskHistoryIndex,
    } = state;

    // バリデーション
    if (!selectedItemImage || furnitureMaskLines.length === 0 || !furnitureMaskStageRef) {
      thunkAPI.dispatch(toastActions.toastError("マスクを描画してください"));
      return;
    }

    const selectedImg = itemImages.find((img) => img.stubId === selectedItemImage);
    if (!selectedImg) {
      thunkAPI.dispatch(toastActions.toastError("画像が選択されていません"));
      return;
    }

    try {
      // マスク画像を生成
      const { generateMaskImage, generatePreviewImage } = await import('../libs/furnitureRemovalMaskUtils');

      // 元画像のサイズを取得するための一時的なImage作成
      const tempImage = new Image();
      tempImage.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        tempImage.onload = resolve;
        tempImage.onerror = reject;
        tempImage.src = furnitureRemovalOriginalImageUrl || selectedImg.uri;
      });

      // マスク画像生成（API用）
      const maskBlob = await generateMaskImage(
        furnitureMaskStageRef,
        tempImage,
        furnitureMaskLines
      );

      // プレビュー画像生成（元画像にマスクを重ねた画像）
      const previewUrl = await generatePreviewImage(
        furnitureRemovalOriginalImageUrl || selectedImg.uri,
        furnitureMaskStageRef,
        furnitureMaskLines
      );

      // 選択中の画像にマスク情報を保存
      thunkAPI.dispatch(itemRemovalActions.saveImageMaskData({
        stubId: selectedItemImage,
        maskData: {
          maskLines: [...furnitureMaskLines],
          maskPreviewUrl: previewUrl,
          maskPreviewBlob: maskBlob,
          maskHistoryStack: furnitureMaskHistoryStack.map(stack => [...stack]),
          maskHistoryIndex: furnitureMaskHistoryIndex,
        }
      }));

      // グローバル状態も更新（表示用）
      thunkAPI.dispatch(itemRemovalActions.setFurnitureMaskPreviewBlob(maskBlob));
      thunkAPI.dispatch(itemRemovalActions.setFurnitureMaskPreviewUrl(previewUrl));

      // 描画ポップアップを閉じる
      thunkAPI.dispatch(itemRemovalActions.setShowFurnitureRemovalDrawing(false));

    } catch (error) {
      console.error('generateFurnitureMaskPreview error:', error);
      const errorMessage = typeof error === 'object' && error !== null && 'message' in error
        ? (error as Error).message
        : 'プレビュー生成中にエラーが発生しました';
      thunkAPI.dispatch(toastActions.toastError(errorMessage));
    }
  }
);

// スライスの作成
const itemRemovalSlice = createSlice({
  name: "itemRemoval",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStubId: (state, action: PayloadAction<number>) => {
      state.stubId = action.payload;
    },
    setSelectedItemImage: (state, action: PayloadAction<number | undefined>) => {

      state.selectedItemImage = action.payload;

      // 選択された元画像を見つける
      if (action.payload !== undefined) {
        const selectedImage = state.itemImages.find(img => img.stubId === action.payload);

        // 選択した画像のマスク情報をグローバル状態に復元
        if (selectedImage && selectedImage.maskData) {
          state.furnitureMaskLines = [...selectedImage.maskData.maskLines];
          state.furnitureMaskPreviewUrl = selectedImage.maskData.maskPreviewUrl;
          state.furnitureMaskPreviewBlob = selectedImage.maskData.maskPreviewBlob;
          state.furnitureMaskHistoryStack = selectedImage.maskData.maskHistoryStack.map(stack => [...stack]);
          state.furnitureMaskHistoryIndex = selectedImage.maskData.maskHistoryIndex;
          // マスク情報がある場合は一部モードを維持
          state.furnitureRemovalMode = 'partial';
        } else if (selectedImage && selectedImage.processedWithPartialMode) {
          // 一部モードで処理された画像の場合は一部モードを維持（マスクはクリア）
          state.furnitureMaskLines = [];
          state.furnitureMaskPreviewUrl = null;
          state.furnitureMaskPreviewBlob = null;
          state.furnitureMaskHistoryStack = [[]];
          state.furnitureMaskHistoryIndex = 0;
          state.furnitureRemovalMode = 'partial';
        } else {
          // マスク情報がない場合はクリアし、全体モードに戻す
          state.furnitureMaskLines = [];
          state.furnitureMaskPreviewUrl = null;
          state.furnitureMaskPreviewBlob = null;
          state.furnitureMaskHistoryStack = [[]];
          state.furnitureMaskHistoryIndex = 0;
          state.furnitureRemovalMode = 'full';
        }

        // 処理済みの場合、対応する処理結果画像を自動選択
        if (selectedImage && selectedImage.isProcessed) {
          // マッチング条件を定義 - stubIdで紐付け（フロントエンドで一意に管理）
          const matchingAfterImages = state.afterImages.filter(img => {
            // stubIdによる紐付け - 同じ元画像から生成された画像
            return img.stubId === selectedImage.stubId;
          });

          if (matchingAfterImages.length > 0) {
            // 最新の処理後画像を選択（IDが大きい方が新しい）
            const latestImage = matchingAfterImages.reduce((latest, current) => {
              return current.id > latest.id ? current : latest;
            }, matchingAfterImages[0]);

            state.selectedAfterImage = latestImage.id;
          } else {
            // マッチする画像がない場合は選択をクリア
            state.selectedAfterImage = undefined;
          }
        } else {
          // 処理済みでない場合は選択をクリア
          state.selectedAfterImage = undefined;
        }
      } else {
        // 選択がクリアされた場合
        state.selectedAfterImage = undefined;
        // マスク情報もクリアし、全体モードに戻す
        state.furnitureMaskLines = [];
        state.furnitureMaskPreviewUrl = null;
        state.furnitureMaskPreviewBlob = null;
        state.furnitureMaskHistoryStack = [[]];
        state.furnitureMaskHistoryIndex = 0;
        state.furnitureRemovalMode = 'full';
      }
    },
    setAfterImages: (state, action: PayloadAction<AfterImage[]>) => {
      state.afterImages = action.payload;
    },
    setIsPhotoTooBigPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isPhotoTooBigPopupShow = action.payload;
    },
    setIsRegenPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isRegenPopupShow = action.payload;
    },
    setIsClearAllPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isClearAllPopupShow = action.payload;
    },
    setRuntime: (state, action: PayloadAction<number>) => {
      state.runTime = action.payload;
    },
    setSelectedRegenImage: (state, action: PayloadAction<number | undefined>) => {
      state.selectedRegenImage = action.payload;
    },
    regenSelectedAfterImage: (state) => {
      // 空の実装 - 実際の実装は非同期アクションクリエーターでされています
    },
    subtractMaxRegen: (state, action: PayloadAction<number>) => {
      const stubId = action.payload;
      state.itemImages.forEach(image => {
        if (image.stubId !== stubId) return;
        if (image.maxRegenCount !== undefined) {
          image.maxRegenCount -= 1;
        }
      });
    },
    clearData: (state) => {
      state.stubId = getRandId();
      state.selectedItemImage = undefined;
      state.selectedAfterImage = undefined;
      state.itemImages = [];
      state.afterImages = [];
      state.isLoading = false;
      state.isConfirmPopupShow = false;
      state.isPhotoTooBigPopupShow = false;
      state.isRegenPopupShow = false;
      state.isClearAllPopupShow = false;
      state.maxExecTimePerImage = 40; // 40秒に設定
      state.runTime = 0;
      state.selectedRegenImage = undefined;
      state.afterImageRef = null;
      state.beforeImageRef = null;
      // ウォーターマーク関連
      state.watermarkGlobalEnabled = false;
      state.processingWatermarks = [];
      // 部分消去機能関連
      state.furnitureRemovalMode = 'full';
      state.showFurnitureRemovalDrawing = false;
      state.furnitureMaskLines = [];
      state.tempFurnitureMaskLines = [];
      state.furnitureBrushHead = 'circle';
      state.furnitureBrushSize = 50;
      state.furnitureMaskHistoryStack = [[]];
      state.furnitureMaskHistoryIndex = 0;
      state.showFurnitureRemovalConfirm = false;
      state.isFurnitureRemovalProcessing = false;
      state.furnitureRemovalOriginalImageUrl = null;
      state.furnitureMaskStageRef = null;
      state.furnitureMaskPreviewUrl = null;
      state.furnitureMaskPreviewBlob = null;
      state.furnitureMaskIsReEdit = false;
    },
    setItemImages: (state, action: PayloadAction<ItemImage[]>) => {
      state.itemImages = action.payload;
    },
    setSelectedAfterImage: (state, action: PayloadAction<number | undefined>) => {
      state.selectedAfterImage = action.payload;
    },
    setIsConfirmPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isConfirmPopupShow = action.payload;
    },
    setAfterImageRef: (state, action: PayloadAction<HTMLImageElement | null>) => {
      state.afterImageRef = action.payload;
    },
    setBeforeImageRef: (state, action: PayloadAction<HTMLImageElement | null>) => {
      state.beforeImageRef = action.payload;
    },
    // ウォーターマーク関連
    setWatermarkGlobalEnabled: (state, action: PayloadAction<boolean>) => {
      state.watermarkGlobalEnabled = action.payload;
    },
    setImageWatermarkStatus: (state, action: PayloadAction<{
      imageId: number,
      enabled: boolean,
      watermarkedURI?: string,
    }>) => {
      const image = state.afterImages.find(img => img.id === action.payload.imageId)
      if (image) {
        image.watermarkEnabled = action.payload.enabled
        if (action.payload.watermarkedURI !== undefined) {
          image.watermarkedURI = action.payload.watermarkedURI
        }
      }
    },
    setImageWatermarkProcessing: (state, action: PayloadAction<{
      imageId: number,
      isProcessing: boolean,
    }>) => {
      const image = state.afterImages.find(img => img.id === action.payload.imageId)
      if (image) {
        image.isProcessingWatermark = action.payload.isProcessing
      }
      // 処理中リストも更新
      if (action.payload.isProcessing) {
        if (!state.processingWatermarks.includes(String(action.payload.imageId))) {
          state.processingWatermarks.push(String(action.payload.imageId))
        }
      } else {
        state.processingWatermarks = state.processingWatermarks.filter(id => id !== String(action.payload.imageId))
      }
    },
    clearAllWatermarks: (state) => {
      state.afterImages.forEach(image => {
        if (image.watermarkedURI) {
          // Data URLを使用しているため、特別なクリーンアップは不要
          image.watermarkedURI = undefined
        }
        image.watermarkEnabled = false
        image.isProcessingWatermark = false
      })
      state.processingWatermarks = []
    },

    // 部分消去機能関連アクション
    setFurnitureRemovalMode: (state, action: PayloadAction<'full' | 'partial'>) => {
      state.furnitureRemovalMode = action.payload;
    },
    setShowFurnitureRemovalDrawing: (state, action: PayloadAction<boolean>) => {
      state.showFurnitureRemovalDrawing = action.payload;
    },
    setFurnitureMaskLines: (state, action: PayloadAction<FurnitureMaskLine[]>) => {
      state.furnitureMaskLines = action.payload;
    },
    setTempFurnitureMaskLines: (state, action: PayloadAction<FurnitureMaskLine[]>) => {
      state.tempFurnitureMaskLines = action.payload;
    },
    applyFurnitureMaskLines: (state) => {
      // 一時線を確定線に移動
      state.furnitureMaskLines = [...state.furnitureMaskLines, ...state.tempFurnitureMaskLines];
      state.tempFurnitureMaskLines = [];
    },
    clearFurnitureMaskLines: (state) => {
      state.furnitureMaskLines = [];
      state.tempFurnitureMaskLines = [];
      state.furnitureMaskHistoryStack = [[]];
      state.furnitureMaskHistoryIndex = 0;
    },
    setFurnitureBrushHead: (state, action: PayloadAction<'circle' | 'square' | 'triangle'>) => {
      state.furnitureBrushHead = action.payload;
    },
    setFurnitureBrushSize: (state, action: PayloadAction<number>) => {
      state.furnitureBrushSize = action.payload;
    },
    pushFurnitureMaskHistory: (state) => {
      // 現在のインデックス以降の履歴を削除し、新しい状態を追加
      const newHistory = state.furnitureMaskHistoryStack.slice(0, state.furnitureMaskHistoryIndex + 1);
      newHistory.push([...state.furnitureMaskLines]);
      state.furnitureMaskHistoryStack = newHistory;
      state.furnitureMaskHistoryIndex = newHistory.length - 1;
    },
    undoFurnitureMask: (state) => {
      if (state.furnitureMaskHistoryIndex > 0) {
        state.furnitureMaskHistoryIndex--;
        state.furnitureMaskLines = [...state.furnitureMaskHistoryStack[state.furnitureMaskHistoryIndex]];
      }
    },
    redoFurnitureMask: (state) => {
      if (state.furnitureMaskHistoryIndex < state.furnitureMaskHistoryStack.length - 1) {
        state.furnitureMaskHistoryIndex++;
        state.furnitureMaskLines = [...state.furnitureMaskHistoryStack[state.furnitureMaskHistoryIndex]];
      }
    },
    setShowFurnitureRemovalConfirm: (state, action: PayloadAction<boolean>) => {
      state.showFurnitureRemovalConfirm = action.payload;
    },
    setIsFurnitureRemovalProcessing: (state, action: PayloadAction<boolean>) => {
      state.isFurnitureRemovalProcessing = action.payload;
    },
    setFurnitureRemovalOriginalImageUrl: (state, action: PayloadAction<string | null>) => {
      state.furnitureRemovalOriginalImageUrl = action.payload;
    },
    setFurnitureMaskStageRef: (state, action: PayloadAction<any>) => {
      state.furnitureMaskStageRef = action.payload;
    },
    setFurnitureMaskPreviewUrl: (state, action: PayloadAction<string | null>) => {
      state.furnitureMaskPreviewUrl = action.payload;
    },
    setFurnitureMaskPreviewBlob: (state, action: PayloadAction<Blob | null>) => {
      state.furnitureMaskPreviewBlob = action.payload;
    },
    clearFurnitureMaskPreview: (state) => {
      state.furnitureMaskPreviewUrl = null;
      state.furnitureMaskPreviewBlob = null;
    },
    setFurnitureMaskIsReEdit: (state, action: PayloadAction<boolean>) => {
      state.furnitureMaskIsReEdit = action.payload;
    },
    // 画像ごとのマスク情報を保存
    saveImageMaskData: (state, action: PayloadAction<{ stubId: number; maskData: ImageMaskData }>) => {
      const { stubId, maskData } = action.payload;
      const imageIndex = state.itemImages.findIndex(img => img.stubId === stubId);
      if (imageIndex !== -1) {
        state.itemImages[imageIndex].maskData = maskData;
      }
    },
    // 画像のマスク情報をクリア
    clearImageMaskData: (state, action: PayloadAction<number>) => {
      const stubId = action.payload;
      const imageIndex = state.itemImages.findIndex(img => img.stubId === stubId);
      if (imageIndex !== -1) {
        state.itemImages[imageIndex].maskData = undefined;
      }
    },
    // 特定のstubIdを持つitemImageのみを更新
    updateItemImageByStubId: (state, action: PayloadAction<{
      stubId: number;
      updates: Partial<ItemImage>;
    }>) => {
      const { stubId, updates } = action.payload;
      const index = state.itemImages.findIndex(img => img.stubId === stubId);
      if (index !== -1) {
        state.itemImages[index] = { ...state.itemImages[index], ...updates };
      }
    },
    // afterImagesに1つの画像を追加
    addAfterImage: (state, action: PayloadAction<AfterImage>) => {
      state.afterImages.push(action.payload);
    },
  },
});

// アクションとリデューサーをエクスポート
export const itemRemovalActions = {
  ...itemRemovalSlice.actions,
  addItemImages,
  processItemRemoval,
  deleteSelectedItem,
  deleteAllItems,
  clearSelectedAfterImage,
  regenSelectedAfterImage,
  applyWatermarkToImage,
  toggleAllWatermarks,
  generateFurnitureMaskPreview,
};

export default itemRemovalSlice.reducer;